import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { MbCard } from "@/components/MbCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Search, Users, CheckCircle, XCircle, Calendar, Upload, UserPlus } from "lucide-react";
import logo from "@/assets/logo.jpg";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ImportUsersDialog } from "@/components/admin/ImportUsersDialog";
import { CreateAdminDialog } from "@/components/admin/CreateAdminDialog";
import { logBulkProfileView, logProfileAccess } from "@/lib/audit-logger";
import { AppFooter } from "@/components/AppFooter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";

interface Subscriber {
  id: string;
  name: string;
  phone: string | null;
  whatsapp: string | null;
  status: string;
  created_at: string;
  subscription_status: string | null;
  subscription_source: string | null;
  plan_code: string | null;
  start_at: string | null;
  end_at: string | null;
}

interface Stats {
  total: number;
  active: number;
  inactive: number;
  activeSubscriptions: number;
}

export default function AdminSubscribers() {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState<Subscriber[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isCreateAdminDialogOpen, setIsCreateAdminDialogOpen] = useState(false);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    active: 0,
    inactive: 0,
    activeSubscriptions: 0,
  });
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin");

    if (!roles || roles.length === 0) {
      navigate("/today");
      return;
    }

    fetchSubscribers();
  };

  const fetchSubscribers = async () => {
    try {
      // Fetch profiles with their subscriptions
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("*")
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch all subscriptions
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from("subscriptions")
        .select("*")
        .order("start_at", { ascending: false });

      if (subscriptionsError) throw subscriptionsError;

      // Join data manually
      const subscribersWithSubs = profilesData.map((profile) => {
        const latestSubscription = subscriptionsData.find(
          (sub) => sub.user_id === profile.id
        );

        return {
          id: profile.id,
          name: profile.name,
          phone: profile.phone,
          whatsapp: profile.whatsapp,
          status: profile.status,
          created_at: profile.created_at,
          subscription_status: latestSubscription?.status || null,
          subscription_source: latestSubscription?.source || null,
          plan_code: latestSubscription?.plan_code || null,
          start_at: latestSubscription?.start_at || null,
          end_at: latestSubscription?.end_at || null,
        };
      });

      setSubscribers(subscribersWithSubs);
      setFilteredSubscribers(subscribersWithSubs);

      // Log bulk profile view for audit
      if (subscribersWithSubs.length > 0) {
        logBulkProfileView(subscribersWithSubs.map(s => s.id));
      }

      // Calculate stats
      const total = subscribersWithSubs.length;
      const active = subscribersWithSubs.filter((s) => s.status === "active").length;
      const inactive = subscribersWithSubs.filter((s) => s.status !== "active").length;
      const activeSubscriptions = subscribersWithSubs.filter(
        (s) => s.subscription_status === "active"
      ).length;

      setStats({ total, active, inactive, activeSubscriptions });
    } catch (error: any) {
      console.error("Error fetching subscribers:", error);
      toast({
        title: "Error",
        description: "Failed to load subscribers",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredSubscribers(subscribers);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = subscribers.filter(
      (sub) =>
        sub.name.toLowerCase().includes(query) ||
        sub.phone?.toLowerCase().includes(query) ||
        sub.whatsapp?.toLowerCase().includes(query) ||
        sub.plan_code?.toLowerCase().includes(query)
    );
    setFilteredSubscribers(filtered);
  }, [searchQuery, subscribers]);

  const getSubscriptionBadge = (status: string | null, source: string | null) => {
    if (!status) {
      return <Badge variant="outline">No Subscription</Badge>;
    }

    if (status === "active") {
      const isComplimentary = source === "complimentary";
      return (
        <Badge className={isComplimentary ? "bg-purple-600 text-white" : "bg-green-600 text-white"}>
          <CheckCircle className="w-3 h-3 mr-1" />
          {isComplimentary ? "Free" : "Active"}
        </Badge>
      );
    }

    if (status === "expired") {
      return (
        <Badge variant="destructive">
          <XCircle className="w-3 h-3 mr-1" />
          Expired
        </Badge>
      );
    }

    return <Badge variant="outline">{status}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    if (status === "active") {
      return <Badge className="bg-green-600 text-white">Active</Badge>;
    }
    return <Badge variant="outline">Inactive</Badge>;
  };

  const handleStatusChange = async (userId: string, newStatus: string) => {
    try {
      // Log the profile update for audit
      await logProfileAccess({ 
        profileId: userId, 
        accessType: 'update' 
      });

      const { error } = await supabase
        .from('profiles')
        .update({ status: newStatus })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `User status updated to ${newStatus}`,
      });

      fetchSubscribers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">લોડ થઈ રહ્યું છે...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="દમયંતી બાગ" className="h-10" />
              <h1 className="text-xl font-bold text-primary">Subscribers</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="mr-2 w-4 h-4" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MbCard>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Subscribers</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-[hsl(var(--mb-orange))]" />
              </div>
            </div>
          </MbCard>

          <MbCard>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Users</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{stats.active}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
          </MbCard>

          <MbCard>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Inactive Users</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{stats.inactive}</p>
                </div>
                <XCircle className="w-8 h-8 text-muted-foreground" />
              </div>
            </div>
          </MbCard>

          <MbCard>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{stats.activeSubscriptions}</p>
                </div>
                <Calendar className="w-8 h-8 text-[hsl(var(--mb-green))]" />
              </div>
            </div>
          </MbCard>
        </div>

        {/* Search Bar and Action Buttons */}
        <MbCard>
          <div className="p-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search by name, phone, or plan..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                onClick={() => setIsCreateAdminDialogOpen(true)}
                variant="outline"
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <UserPlus className="w-4 h-4" />
                Create Admin
              </Button>
              <Button
                onClick={() => setIsImportDialogOpen(true)}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <Upload className="w-4 h-4" />
                Import Users
              </Button>
            </div>
          </div>
        </MbCard>

        {/* Subscribers Table */}
        <MbCard>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>User Status</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSubscribers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {searchQuery ? "No subscribers found matching your search" : "No subscribers yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSubscribers.map((subscriber) => (
                    <TableRow key={subscriber.id}>
                      <TableCell className="font-medium">{subscriber.name}</TableCell>
                      <TableCell>
                        <div className="space-y-1 text-sm">
                          {subscriber.phone && <div>📱 {subscriber.phone}</div>}
                          {subscriber.whatsapp && <div>💬 {subscriber.whatsapp}</div>}
                          {!subscriber.phone && !subscriber.whatsapp && (
                            <span className="text-muted-foreground">No contact</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(subscriber.status)}</TableCell>
                      <TableCell>
                        {getSubscriptionBadge(subscriber.subscription_status, subscriber.subscription_source)}
                      </TableCell>
                      <TableCell>
                        {subscriber.plan_code ? (
                          <span className="text-sm">{subscriber.plan_code}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {subscriber.end_at ? (
                          <span className="text-sm">
                            {format(new Date(subscriber.end_at), "MMM dd, yyyy")}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(subscriber.created_at), "MMM dd, yyyy")}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={subscriber.status}
                          onValueChange={(value) => handleStatusChange(subscriber.id, value)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </MbCard>

        {/* Summary Footer */}
        <div className="text-center text-sm text-muted-foreground">
          Showing {filteredSubscribers.length} of {stats.total} total subscribers
        </div>
      </div>

      {/* Import Dialog */}
      <ImportUsersDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onImportComplete={fetchSubscribers}
      />

      {/* Create Admin Dialog */}
      <CreateAdminDialog
        isOpen={isCreateAdminDialogOpen}
        onClose={() => setIsCreateAdminDialogOpen(false)}
        onSuccess={fetchSubscribers}
      />
      
      <AppFooter />
    </div>
  );
}
